import React, { useEffect, useState } from "react";
import { db } from "../../lib/firebase";
import { getAuth } from "firebase/auth";
import { collection, getDocs, getCountFromServer, query, where } from "firebase/firestore";
import { 
  LuUsers, 
  LuDollarSign, 
  LuCalendar, 
  LuTrendingUp,
  LuActivity,
  LuSettings 
} from "react-icons/lu";
import axios from "axios"; // Ensure axios is imported

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ||
  (window.location.hostname === "localhost" ? "http://localhost:3000" : "");

if (!API_BASE) {
  throw new Error("VITE_API_BASE_URL is missing");
}

// Get auth headers helper
const getAuthHeaders = async () => {
    const auth = getAuth();
    if (!auth.currentUser) throw new Error("Not authenticated");
    const token = await auth.currentUser.getIdToken();
    return { Authorization: `Bearer ${token}` };
};

export const Dashboard = () => {
  const [data, setData] = useState({
    events: [],
    globalStats: {
      totalParticipants: 0,
      verifiedParticipants: 0,
      totalRevenue: 0,
      totalNetRevenue: 0,
      totalRevenueUSD: 0,
      totalNetRevenueUSD: 0,
      activeEvents: 0
    },
    loading: true
  });
  const [settingsModal, setSettingsModal] = useState({
    isOpen: false,
    eventId: null,
    enabled: false,
    deadline: ""
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const currentDate = new Date().toLocaleDateString('id-ID', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1. Fetch Events
        const eventsRef = collection(db, "semuaKegiatan");
        const eventsSnapshot = await getDocs(eventsRef);
        const eventsList = eventsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // 1. Fetch Refunded Transactions via API (Bypass Firestore permission issues)
        // DYNAMIC: No manual exclusions. 
        const refundedOrderIds = new Set();
        try {
           const headers = await getAuthHeaders();
           const { data: refundData } = await axios.get(`${API_BASE}/api/export-refunds`, { headers });
           if (Array.isArray(refundData)) {
              refundData.forEach((r) => {
                 if (r.orderId) refundedOrderIds.add(r.orderId);
              });
           }
        } catch (error) {
           console.warn("Could not fetch refunded transactions via API.", error);
        }

        const detailedEventsPromises = eventsList.map(async (event) => {
          const participantsRef = collection(db, `pendaftaran${event.id}`);
          const participantsSnapshot = await getDocs(participantsRef);
          // ... rest of logic stays same ...
          let eventTotalCount = 0;
          let eventVerifiedCount = 0;
          let eventTotalRevenue = 0;
          let eventTotalRevenueUSD = 0;
          let eventNetRevenue = 0;
          let eventNetRevenueUSD = 0;
          const categoryStats = {};

          // Pre-populate with current event categories from Firestore doc
          const currentCategoryNames = new Set();
          if (Array.isArray(event.categories)) {
             event.categories.forEach(cat => {
                const name = cat.categoryName;
                currentCategoryNames.add(name);
                categoryStats[name] = { 
                   count: 0, 
                   verifiedCount: 0, 
                   paidCount: 0,
                   freeCount: 0,
                   revenue: 0, netRevenue: 0,
                   revenueUSD: 0, netRevenueUSD: 0 
                };
             });
          }

          participantsSnapshot.forEach((doc) => {
             const p = doc.data();

             // STRICT EXCLUSION: Skip Refunds
             if (refundedOrderIds.has(p.orderId) || refundedOrderIds.has(doc.id)) {
                return;
             }

             // Attribute BOTH count and revenue to current kategori so every row
             // is internally consistent (count × avg price ≈ revenue). Money
             // follows the runner — policy: category transfer has no refund,
             // so participant's payment stays with them in their current category.
             const currentCategory = p.kategori || "Uncategorized";
             const revenueCategory = currentCategory;
             const isSettled = p.transaction_status === "settlement";

             // GAS Logic
             const isPayPalCompleted = p.paypalCapture?.status === 'COMPLETED';
             const isPayPalType = p.paymentMethod === 'paypal' || p.payment_type === 'paypal';
             const isPayPal = isPayPalCompleted || (isPayPalType && isSettled);
             const isFree = p.payment_type === 'free' || Number(p.harga) === 0 || !p.harga;


             if (!categoryStats[currentCategory]) {
                categoryStats[currentCategory] = {
                   count: 0,
                   verifiedCount: 0,
                   paidCount: 0,
                   freeCount: 0,
                   revenue: 0, netRevenue: 0,
                   revenueUSD: 0, netRevenueUSD: 0
                };
             }
             if (!categoryStats[revenueCategory]) {
                categoryStats[revenueCategory] = {
                   count: 0,
                   verifiedCount: 0,
                   paidCount: 0,
                   freeCount: 0,
                   revenue: 0, netRevenue: 0,
                   revenueUSD: 0, netRevenueUSD: 0
                };
             }

             // Count all participants based on CURRENT category (race logistics)
             categoryStats[currentCategory].count += 1;
             eventTotalCount += 1;

             // Active Free Logic: Free users are verified unless expired/canceled
             const isActiveFree = isFree && p.transaction_status !== 'expire' && p.transaction_status !== 'cancel' && p.transaction_status !== 'refund';

             if (isSettled || isPayPalCompleted || isActiveFree) {
                // Verified Logic
                if (isPayPal) {
                   // PayPal Logic (USD) - Robust Sum
                   let amountUSD = 0;
                   let netUSD = 0;
                   try {
                      const captures = p.paypalCapture?.purchase_units?.[0]?.payments?.captures;
                      if (captures && Array.isArray(captures)) {
                         captures.forEach(cap => {
                             amountUSD += parseFloat(cap.amount?.value || 0);
                             const breakdown = cap.seller_receivable_breakdown;
                             if (breakdown && breakdown.net_amount) {
                                 netUSD += parseFloat(breakdown.net_amount.value || 0);
                             } else {
                                 const fee = parseFloat(breakdown?.paypal_fee?.value || 0);
                                 netUSD += (parseFloat(cap.amount?.value || 0) - fee);
                             }
                         });
                      }
                   } catch (e) { }

                   categoryStats[currentCategory].verifiedCount += 1;
                   categoryStats[currentCategory].paidCount += 1;
                   eventVerifiedCount += 1;

                   eventTotalRevenueUSD += amountUSD;
                   categoryStats[revenueCategory].revenueUSD += amountUSD;

                   eventNetRevenueUSD += netUSD;
                   categoryStats[revenueCategory].netRevenueUSD += netUSD;

                } else if (isActiveFree) {
                   categoryStats[currentCategory].verifiedCount += 1;
                   categoryStats[currentCategory].freeCount += 1;
                   eventVerifiedCount += 1;
                   // No revenue for free
                } else {
                   categoryStats[currentCategory].verifiedCount += 1;
                   categoryStats[currentCategory].paidCount += 1;
                   eventVerifiedCount += 1;

                   // Midtrans Logic (IDR)
                   // gross_amount = original registration payment (never modified, auditable vs Midtrans dashboard)
                   // categoryTransferHistory[].amountPaid = each upgrade transfer payment (auditable vs TRANSFER-* in Midtrans)
                   const registrationAmount = Number(p.gross_amount) || Number(p.harga) || 0;
                   const transferAmount = Array.isArray(p.categoryTransferHistory)
                      ? p.categoryTransferHistory
                           .filter(h => h.transferType === "upgrade" && Number(h.amountPaid) > 0)
                           .reduce((sum, h) => sum + Number(h.amountPaid), 0)
                      : 0;
                   const amountIDR = registrationAmount + transferAmount;
                   const midtransFee = 4440;
                   // Midtrans fee applies per transaction (1 registration + N transfer payments)
                   const txCount = 1 + (Array.isArray(p.categoryTransferHistory) ? p.categoryTransferHistory.filter(h => h.transferType === "upgrade" && Number(h.amountPaid) > 0).length : 0);

                   eventTotalRevenue += amountIDR;
                   categoryStats[revenueCategory].revenue += amountIDR;

                   const netIDR = Math.max(0, amountIDR - midtransFee * txCount);
                   eventNetRevenue += netIDR;
                   categoryStats[revenueCategory].netRevenue += netIDR;
                }
             }
          });

          // Convert category map to array for easier rendering
          const categoriesArray = Object.entries(categoryStats)
             .map(([name, stats]) => ({
                name,
                ...stats
             }))
             .filter(cat => {
                // Keep if it's in the current event config
                if (currentCategoryNames.has(cat.name)) return true;
                // Keep if it has verified members (legacy data with value)
                if (cat.verifiedCount > 0) return true;
                // Otherwise hide (ghost categories with 0 confirmed)
                return false;
             })
             .sort((a, b) => {
                // Sort by highest revenue, then by count
                if (b.revenue !== a.revenue) return b.revenue - a.revenue;
                return b.count - a.count;
             });

          return {
            ...event,
            stats: {
               totalCount: eventTotalCount,
               verifiedCount: eventVerifiedCount,
               paidCount: categoriesArray.reduce((acc, c) => acc + c.paidCount, 0),
               freeCount: categoriesArray.reduce((acc, c) => acc + c.freeCount, 0),
               totalRevenue: eventTotalRevenue,
               totalNetRevenue: eventNetRevenue,
               totalRevenueUSD: eventTotalRevenueUSD,
               totalNetRevenueUSD: eventNetRevenueUSD,
               categories: categoriesArray
            }
          };
        });

        const detailedEvents = await Promise.all(detailedEventsPromises);

        // 3. Aggregate Global Stats
        const globalParticipants = detailedEvents.reduce((acc, curr) => acc + curr.stats.totalCount, 0);
        const globalVerifiedParticipants = detailedEvents.reduce((acc, curr) => acc + curr.stats.verifiedCount, 0);
        const globalRevenue = detailedEvents.reduce((acc, curr) => acc + curr.stats.totalRevenue, 0);
        const globalNetRevenue = detailedEvents.reduce((acc, curr) => acc + curr.stats.totalNetRevenue, 0);
        const globalRevenueUSD = detailedEvents.reduce((acc, curr) => acc + curr.stats.totalRevenueUSD, 0);
        const globalNetRevenueUSD = detailedEvents.reduce((acc, curr) => acc + curr.stats.totalNetRevenueUSD, 0);

        setData({
          events: detailedEvents,
          globalStats: {
            totalParticipants: globalParticipants,
            verifiedParticipants: globalVerifiedParticipants,
            totalRevenue: globalRevenue,
            totalNetRevenue: globalNetRevenue,
            totalRevenueUSD: globalRevenueUSD,
            totalNetRevenueUSD: globalNetRevenueUSD,
            activeEvents: eventsList.length
          },
          loading: false
        });

      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
        // Important: Still disable loading state to allow UI to render (or show empty state)
        setData(prev => ({ ...prev, loading: false }));
      }
    };

    fetchData();
  }, []);

  if (data.loading) {
     return (
        <div className="flex flex-col gap-4 animate-pulse p-4">
           <div className="h-24 w-full bg-gray-200 rounded-xl"></div>
           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="h-32 bg-gray-200 rounded-xl"></div>
              <div className="h-32 bg-gray-200 rounded-xl"></div>
              <div className="h-32 bg-gray-200 rounded-xl"></div>
           </div>
           <div className="h-96 bg-gray-200 rounded-xl"></div>
        </div>
     );
  }

  // Helper to format currency (SAFE version)
  const formatIDR = (val) => `Rp ${(Number(val) || 0).toLocaleString('id-ID')}`;
  const formatUSD = (val) => `$${(Number(val) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  return (
    <div className="space-y-4 pb-10">
      {/* Hero Section */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
         <div className="absolute top-0 right-0 p-4 opacity-5 text-slate-200 pointer-events-none">
            <LuActivity size={100} />
         </div>
         <div className="relative z-10">
            <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
               <LuCalendar size={14} /> {currentDate}
            </p>
            <h1 className="text-2xl font-bold mb-1">Good Morning, Admin 👋</h1>
            <p className="text-slate-500 text-sm max-w-xl">
               Overview of your event performance. Showing <strong>Confirmed (Paid & Free)</strong> participants.
            </p>
         </div>
      </div>

      {/* Global Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatsCard 
           title="Confirmed Participants" 
           value={(data.globalStats.verifiedParticipants || 0).toLocaleString()} 
           icon={<LuUsers size={20} />}
           trend="Active (Paid + Free)"
           color="bg-slate-700"
           subValue={`${data.globalStats.totalParticipants || 0} Total Registered`}
        />
        <StatsCard 
           title="IDR Revenue" 
           value={formatIDR(data.globalStats.totalRevenue)} 
           icon={<LuDollarSign size={20} />}
           trend="Midtrans / Bank Transfer"
           color="bg-slate-700"
           subValue={`Net: ${formatIDR(data.globalStats.totalNetRevenue)}`}
        />
        <StatsCard 
           title="USD Revenue (PayPal)" 
           value={formatUSD(data.globalStats.totalRevenueUSD)} 
           icon={<LuDollarSign size={20} />}
           trend="International"
           color="bg-slate-700"
           subValue={`Net: ${formatUSD(data.globalStats.totalNetRevenueUSD)}`}
        />
        <StatsCard 
           title="Active Events" 
           value={data.globalStats.activeEvents} 
           icon={<LuTrendingUp size={20} />}
           trend="Currently running"
           color="bg-slate-700"
        />
      </div>

      {/* Detailed Event Performance */}
      <div className="space-y-4">
         <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <LuActivity className="text-slate-700" size={20} /> Event Performance
         </h2>
         
         <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {data.events.map((event) => (
               <div key={event.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
                     <div className="p-4 border-b border-gray-100 bg-slate-50 flex justify-between items-center">
                        <div>
                           <h3 className="font-bold text-base text-gray-900">{event.title || event.id}</h3>
                           <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs text-gray-500">ID: {event.id}</p>
                              <button 
                                onClick={async () => {
                                  // Fetch current settings before opening
                                  try {
                                    const headers = await getAuthHeaders();
                                    const { data } = await axios.get(`${API_BASE}/api/category-transfer/config/${event.id}`, { headers });
                                    setSettingsModal({
                                      isOpen: true,
                                      eventId: event.id,
                                      enabled: data.enabled,
                                      deadline: data.deadline ? new Date(data.deadline).toISOString().split('T')[0] : ""
                                    });
                                  } catch (e) {
                                    console.error("Error fetching config", e);
                                    // Fallback open with defaults
                                    setSettingsModal({
                                      isOpen: true,
                                      eventId: event.id,
                                      enabled: false,
                                      deadline: ""
                                    });
                                  }
                                }}
                                className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-800 bg-white border border-slate-200 px-2 py-0.5 rounded shadow-sm transition-colors"
                              >
                                <LuSettings size={10} /> Config Transfer
                              </button>
                           </div>
                        </div>
                        <span className="bg-slate-900 text-white text-xs font-bold px-3 py-1 rounded-md">
                           {event.stats.verifiedCount} Confirmed
                        </span>
                     </div>
                  
                  <div className="flex-1 p-0 overflow-x-auto">
                     <table className="w-full text-left border-collapse">
                        <thead>
                           <tr className="bg-gray-50 border-b border-gray-100">
                              <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                              <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Paid</th>
                              <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Free</th>
                              <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Revenue (IDR)</th>
                              <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Revenue (USD)</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                           {event.stats.categories.map((cat, idx) => (
                              <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                 <td className="px-4 py-2 text-sm font-medium text-gray-700">
                                    <div className="flex items-center gap-2">
                                       <div className={`w-1.5 h-1.5 rounded-full ${idx === 0 ? 'bg-amber-400' : 'bg-gray-300'}`}></div>
                                       {cat.name}
                                    </div>
                                 </td>
                                 <td className="px-4 py-2 text-sm text-gray-600 text-right font-medium">
                                    {(cat.paidCount || 0).toLocaleString()}
                                 </td>
                                 <td className="px-4 py-2 text-sm text-gray-500 text-right">
                                    {cat.freeCount > 0 ? (cat.freeCount || 0).toLocaleString() : "-"}
                                 </td>
                                 <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                    <div className="font-bold">{formatIDR(cat.revenue || 0)}</div>
                                    <div className="text-xs text-gray-400">Net: {formatIDR(cat.netRevenue || 0)}</div>
                                 </td>
                                 <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                    <div className="font-bold">{formatUSD(cat.revenueUSD || 0)}</div>
                                    <div className="text-xs text-gray-400">Net: {formatUSD(cat.netRevenueUSD || 0)}</div>
                                 </td>
                              </tr>
                           ))}
                           {event.stats.categories.length === 0 && (
                              <tr>
                                 <td colSpan="5" className="px-4 py-4 text-center text-sm text-gray-400 italic">No settlement data available</td>
                              </tr>
                           )}
                        </tbody>
                        <tfoot className="bg-gray-50">
                           <tr>
                              <td className="px-4 py-2 text-sm font-bold text-gray-800">Total</td>
                              <td className="px-4 py-2 text-sm font-bold text-gray-800 text-right">
                                 {(event.stats.paidCount || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-sm font-bold text-gray-400 text-right">
                                 {(event.stats.freeCount || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-sm text-right">
                                 <div className="font-bold text-slate-800">{formatIDR(event.stats.totalRevenue || 0)}</div>
                                 <div className="text-xs text-gray-400 font-semibold">Net: {formatIDR(event.stats.totalNetRevenue || 0)}</div>
                              </td>
                               <td className="px-4 py-2 text-sm text-right">
                                 <div className="font-bold text-slate-800">{formatUSD(event.stats.totalRevenueUSD || 0)}</div>
                                 <div className="text-xs text-gray-400 font-semibold">Net: {formatUSD(event.stats.totalNetRevenueUSD || 0)}</div>
                              </td>
                           </tr>
                        </tfoot>
                     </table>
                  </div>
               </div>
            ))}
         </div>
      </div>

      {/* Settings Modal */}
      {settingsModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
             <div className="p-4 border-b border-slate-100 flex justify-between items-center">
                <h3 className="font-bold text-slate-800">Category Transfer Settings</h3>
                <button onClick={() => setSettingsModal(prev => ({...prev, isOpen: false}))} className="text-slate-400 hover:text-slate-600">&times;</button>
             </div>
             
             <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                   <label className="text-sm font-medium text-slate-700">Enable Feature</label>
                   <button 
                      onClick={() => setSettingsModal(prev => ({...prev, enabled: !prev.enabled}))}
                      className={`w-12 h-6 rounded-full transition-colors relative ${settingsModal.enabled ? 'bg-slate-900' : 'bg-slate-200'}`}
                   >
                      <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-all ${settingsModal.enabled ? 'left-7' : 'left-1'}`}></div>
                   </button>
                </div>

                <div>
                   <label className="block text-sm font-medium text-slate-700 mb-1">Deadline (End Date)</label>
                   <input 
                      type="date" 
                      value={settingsModal.deadline}
                      onChange={(e) => setSettingsModal(prev => ({...prev, deadline: e.target.value}))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-400 focus:border-transparent"
                   />
                   <p className="text-xs text-slate-500 mt-1">Participants cannot transfer after this date.</p>
                </div>
             </div>

             <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
                <button 
                  onClick={() => setSettingsModal(prev => ({...prev, isOpen: false}))}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={async () => {
                     setSavingSettings(true);
                     try {
                        const headers = await getAuthHeaders();
                        await axios.post(`${API_BASE}/api/category-transfer/config/${settingsModal.eventId}`, {
                           enabled: settingsModal.enabled,
                           deadline: settingsModal.deadline
                        }, { headers });
                        setSettingsModal(prev => ({...prev, isOpen: false}));
                        alert("Settings saved!");
                     } catch (e) {
                        alert("Failed to save settings: " + e.message);
                     } finally {
                        setSavingSettings(false);
                     }
                  }}
                  disabled={savingSettings}
                  className="px-4 py-2 text-sm font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
                >
                  {savingSettings ? "Saving..." : "Save Changes"}
                </button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatsCard = ({ title, value, icon, trend, color, subValue }) => (
   <div className="bg-white p-4 rounded-xl border border-gray-200 flex items-start justify-between">
      <div>
         <p className="text-gray-500 text-xs font-medium mb-1 uppercase tracking-wider">{title}</p>
         <h3 className="text-2xl font-bold text-slate-800 mb-1">{value}</h3>
         <p className="text-[10px] text-slate-700 font-medium bg-slate-100 inline-block px-1.5 py-0.5 rounded">
            {trend}
         </p>
         {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
      </div>
      <div className="text-slate-400">
         {icon}
      </div>
   </div>
);
